import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Canvas from "./Canvas";
import Sidebar from "./Sidebar";
import RoomManager from "./RoomManager";
import CurvePopup from "./CurvePopup";
import MergePopup from "./MergePopup";
import UnmergePopup from "./UnmergePopup";
import DoorSnapPopup from "./DoorSnapPopup";
import EstimateModal from "./EstimateModal";
import SaveModal from "./SaveModal";
import LoadModal from "./LoadModal";
import RectangleNameModal from "./RectangleNameModal";
import FlooringPatterns from "./FlooringPatterns";
import Highlighter from "./Highlighter";
import AnnotationTools from "./AnnotationTools";
import MeasurementTools from "./MeasurementTools";
import TotalSquareFootage from "./TotalSquareFootage";
import ThicknessPopup from './ThicknessPopup';
import RoomNameModal from './RoomNameModal';
import {
  FaQuestionCircle,
  FaHome,
  FaBook,
  FaChartBar,
  FaTimes,
  FaBars,
  FaSave,
  FaFolderOpen,
} from "react-icons/fa";
import { useApp } from "../context/AppContext";
import "./FloorPlanner.css";
import EditableLine from "./shapes/EditableLine";
import { RiDeleteBin2Line } from "react-icons/ri";

const FloorPlanner = () => {
  const { shapes, selectedShapeId, mergedStructures, clearStoredState, updateShape, deleteShape, undo,addConnection, removeShapes, setMergedStructures } =
    useApp();
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  // State to control Room Manager visibility
  const [showRoomManager, setShowRoomManager] = useState(false);
  const [screenSize, setScreenSize] = useState("desktop");

  // State for modals
  const [isEstimateModalOpen, setIsEstimateModalOpen] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showRectangleNameModal, setShowRectangleNameModal] = useState(false);
  const [editingRectangle, setEditingRectangle] = useState(null);
  
  // State for room name modal
  const [showRoomNameModal, setShowRoomNameModal] = useState(false);
  const [editingRoomName, setEditingRoomName] = useState(null);

  // State for new features
  const [selectedFlooringPattern, setSelectedFlooringPattern] = useState(null);
  const [selectedHighlightColor, setSelectedHighlightColor] = useState(null);
  const [isHighlightMode, setIsHighlightMode] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingLineWidth, setDrawingLineWidth] = useState(8);
  const [selectedAnnotationTool, setSelectedAnnotationTool] = useState(null);
  const [selectedMeasurementTool, setSelectedMeasurementTool] = useState(null);
  const [selectedMeasurementUnit, setSelectedMeasurementUnit] = useState({
    id: 'meters',
    name: 'Meters',
    symbol: 'm',
    abbreviation: 'm',
    description: 'Metric system - meters',
    conversionFactor: 1,
  });
  const [showSquareFootage, setShowSquareFootage] = useState(true);
  const [showInsideDimensions, setShowInsideDimensions] = useState(false);
  const [showOutsideDimensions, setShowOutsideDimensions] = useState(false);
  
  // Grid and blueprint mode state
  const [showGrid, setShowGrid] = useState(false);
  const [isBlueprintMode, setIsBlueprintMode] = useState(false);

  // State for undo/redo stacks
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  
  // State for thickness popup
  const [showThicknessPopup, setShowThicknessPopup] = useState(false);
  const [currentWallThickness, setCurrentWallThickness] = useState(5);
  
  // State for wall alignment
  const [wallAlignmentEnabled, setWallAlignmentEnabled] = useState(true);

  // State for toolbar visibility
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);

  // State for annotations
  const [annotations, setAnnotations] = useState([]);

  // Helper functions to generate rooms data for EstimateModal
  const calculateShapeArea = (shape) => {
    switch (shape.type) {
      case "rectangle":
        if (
          shape.lineData &&
          shape.lineData.some((line) => line && line.isCurveMode)
        ) {
          const baseArea = (shape.width * shape.height) / 10000;
          const curveCount = shape.lineData.filter(
            (line) => line && line.isCurveMode
          ).length;
          const curveAdjustment = curveCount * 0.1;
          return baseArea * (1 + curveAdjustment);
        }
        return (shape.width * shape.height) / 10000;
      case "circle":
        return Math.PI * Math.pow(shape.radius / 100, 2);
      case "triangle":
        return (shape.width * shape.height) / 2 / 10000;
      case "door":
        const radius = shape.radius || 50;
        return (Math.PI * Math.pow(radius / 100, 2)) / 2;
      default:
        return 0;
    }
  };

  const calculateShapePerimeter = (shape) => {
    switch (shape.type) {
      case "rectangle":
        return 2 * ((shape.width + shape.height) / 100);
      case "circle":
        return 2 * Math.PI * (shape.radius / 100);
      case "triangle":
        const side1 = shape.width / 100;
        const side2 = shape.height / 100;
        const side3 = Math.sqrt(Math.pow(side1, 2) + Math.pow(side2, 2));
        return side1 + side2 + side3;
      case "door":
        const radius = shape.radius || 50;
        return Math.PI * (radius / 100);
      default:
        return 0;
    }
  };

  const getEquivalentDimensions = (shape) => {
    switch (shape.type) {
      case "rectangle":
        return { width: shape.width / 100, height: shape.height / 100 };
      case "circle":
        const diameter = (shape.radius * 2) / 100;
        return { width: diameter, height: diameter };
      case "triangle":
        return { width: shape.width / 100, height: shape.height / 100 };
      case "door":
        const doorDiameter = ((shape.radius || 50) * 2) / 100;
        return { width: doorDiameter, height: doorDiameter };
      default:
        return { width: 1, height: 1 };
    }
  };

  const createIndividualRooms = (shapes) => {
    return shapes
      .filter((shape) => ["rectangle", "polygonRoom", "circle", "triangle", "door"].includes(shape.type))
      .map((shape, index) => {
        let area = 0, perimeter = 0, width = 0, height = 0, pos = { x: 0, y: 0 };
        if (shape.type === "polygonRoom" && shape.vertices && shape.vertices.length > 2) {
          // Area
          for (let i = 0, j = shape.vertices.length - 1; i < shape.vertices.length; j = i++) {
            area += (shape.vertices[j].x + shape.vertices[i].x) * (shape.vertices[j].y - shape.vertices[i].y);
          }
          area = Math.abs(area / 2) / 10000;
          // Perimeter
          for (let i = 0, j = shape.vertices.length - 1; i < shape.vertices.length; j = i++) {
            const dx = shape.vertices[i].x - shape.vertices[j].x;
            const dy = shape.vertices[i].y - shape.vertices[j].y;
            perimeter += Math.sqrt(dx * dx + dy * dy);
          }
          perimeter = perimeter / 100;
          // Bounds
          const xs = shape.vertices.map(v => v.x);
          const ys = shape.vertices.map(v => v.y);
          width = (Math.max(...xs) - Math.min(...xs)) / 100;
          height = (Math.max(...ys) - Math.min(...ys)) / 100;
          pos = { x: Math.min(...xs), y: Math.min(...ys) };
        } else {
          area = calculateShapeArea(shape);
          perimeter = calculateShapePerimeter(shape);
          const dimensions = getEquivalentDimensions(shape);
          width = dimensions.width;
          height = dimensions.height;
          pos = { x: shape.x, y: shape.y };
        }
        return {
          id: `individual-${shape.id}`,
          name: `Room ${index + 1}`,
          area: area.toFixed(2),
          perimeter: perimeter.toFixed(2),
          width: width.toFixed(1),
          height: height.toFixed(1),
          position: pos,
          type: "individual",
          shapeType: shape.type,
          carpetComplexity: "simple",
          originalShape: shape,
        };
      });
  };

  const calculateAccurateBounds = (shapes) => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    let totalArea = 0;
    let totalPerimeter = 0;

    shapes.forEach((shape) => {
      const area = calculateShapeArea(shape);
      const perimeter = calculateShapePerimeter(shape);
      totalArea += area;
      totalPerimeter += perimeter;

      const left = shape.x;
      const top = shape.y;
      const right = shape.x + (shape.width || shape.radius * 2 || 0);
      const bottom = shape.y + (shape.height || shape.radius * 2 || 0);

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });

    return {
      x: minX,
      y: minY,
      width: (maxX - minX) / 100,
      height: (maxY - minY) / 100,
      area: totalArea,
      perimeter: totalPerimeter,
    };
  };

  const createMergedRooms = (structures) => {
    return structures.map((structure, index) => {
      const bounds = calculateAccurateBounds(structure.shapes);
      const hasNonRectangular = structure.shapes.some(
        (s) => s.type !== "rectangle"
      );
      const hasCurvedRectangles = structure.shapes.some(
        (s) =>
          s.type === "rectangle" &&
          s.lineData &&
          s.lineData.some((line) => line && line.isCurveMode)
      );

      return {
        id: `merged-${index}`,
        name: `Complex Room ${index + 1}`,
        area: bounds.area.toFixed(2),
        perimeter: bounds.perimeter.toFixed(2),
        width: bounds.width.toFixed(1),
        height: bounds.height.toFixed(1),
        position: { x: bounds.x, y: bounds.y },
        type: "merged",
        complexity: structure.shapes.length,
        hasNonRectangular,
        hasCurvedRectangles,
        shapes: structure.shapes,
      };
    });
  };

  const getAvailableShapes = () => {
    const mergedShapeIds = new Set();
    if (mergedStructures) {
      mergedStructures.forEach((structure) => {
        structure.shapes.forEach((shape) => {
          mergedShapeIds.add(shape.id);
        });
      });
    }
    return shapes.filter((shape) => !mergedShapeIds.has(shape.id));
  };

  const generateRoomsData = () => {
    try {
      const availableShapes = getAvailableShapes();
      const individualRooms = createIndividualRooms(availableShapes);
      const mergedRooms = mergedStructures
        ? createMergedRooms(mergedStructures)
        : [];
      const rooms = [...individualRooms, ...mergedRooms];

      console.log("🏠 ROOMS DATA GENERATED:", {
        individualRooms: individualRooms.length,
        mergedRooms: mergedRooms.length,
        totalRooms: rooms.length,
      });

      return rooms;
    } catch (error) {
      console.error("❌ Error generating rooms data:", error);
      return []; // Return empty array on error
    }
  };

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 600) {
        setScreenSize("mobile");
      } else if (width <= 1000) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Toggle Room Manager visibility
  const toggleRoomManager = useCallback(() => {
    setShowRoomManager((prev) => !prev);
  }, []);

  // Handle merge confirmation from popup - forward to Canvas
  const handleMergeConfirm = useCallback((mergeData) => {
    console.log(
      "📋 APP LEVEL - Merge confirmed, forwarding to Canvas:",
      mergeData
    );

    // Forward the merge to Canvas component
    if (canvasRef.current && canvasRef.current.handleMergeConfirm) {
      canvasRef.current.handleMergeConfirm(mergeData);
    }
  }, []);

  // Handle unmerge confirmation from popup - forward to Canvas
  const handleUnmergeConfirm = useCallback((structureId) => {
    console.log(
      "📋 APP LEVEL - Unmerge confirmed, forwarding to Canvas:",
      structureId
    );

    // Forward the unmerge to Canvas component
    if (canvasRef.current && canvasRef.current.handleUnmergeConfirm) {
      canvasRef.current.handleUnmergeConfirm(structureId);
    }
  }, []);

  // Handle door snap confirmation from popup
  const handleDoorSnapConfirm = useCallback((confirmed, snapData) => {
    console.log("📋 APP LEVEL - Door snap response:", { confirmed, snapData });

    // Dispatch the response back to the Door component
    window.dispatchEvent(
      new CustomEvent("doorSnapResponse", {
        detail: { confirmed, snapData },
      })
    );
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Estimate modal shortcut
      if (event.ctrlKey && event.key === "e") {
        event.preventDefault();
        setIsEstimateModalOpen(true);
      }

      // Debug info shortcut
      if (event.ctrlKey && event.shiftKey && event.key === "D") {
        event.preventDefault();
        setShowDebugInfo(!showDebugInfo);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showDebugInfo]);

  // Event listeners for save and load modals
  useEffect(() => {
    const handleShowSaveModal = () => {
      setShowSaveModal(true);
    };

    const handleShowLoadModal = () => {
      setShowLoadModal(true);
    };

    const handleApplyPatternToMergedStructure = (event) => {
      const { structureId, pattern } = event.detail;
      console.log('📋 APP LEVEL - Applying pattern to merged structure:', structureId, pattern.name);
      
      // Forward the pattern application to Canvas component
      if (canvasRef.current && canvasRef.current.applyPatternToMergedStructure) {
        canvasRef.current.applyPatternToMergedStructure(structureId, pattern);
      } else {
        // Alternative: dispatch event to Canvas
        window.dispatchEvent(new CustomEvent('updateMergedStructurePattern', {
          detail: { structureId, pattern }
        }));
      }
    };

    window.addEventListener('showSaveModal', handleShowSaveModal);
    window.addEventListener('showLoadModal', handleShowLoadModal);
    window.addEventListener('applyPatternToMergedStructure', handleApplyPatternToMergedStructure);

    return () => {
      window.removeEventListener('showSaveModal', handleShowSaveModal);
      window.removeEventListener('showLoadModal', handleShowLoadModal);
      window.removeEventListener('applyPatternToMergedStructure', handleApplyPatternToMergedStructure);
    };
  }, []);

  // Handle save modal callbacks
  const handleSaveModalClose = useCallback(() => {
    setShowSaveModal(false);
  }, []);

  const handleSaveModalSave = useCallback((fileName, saveForFuture) => {
    console.log('Plan saved:', { fileName, saveForFuture });
    // The actual saving is handled in the SaveModal component
  }, []);

  // Handle load modal callbacks
  const handleLoadModalClose = useCallback(() => {
    setShowLoadModal(false);
  }, []);

  const handleLoadModalLoad = useCallback((planData) => {
    console.log('Plan loaded:', planData);
    // Refresh the page to load the new data
    window.location.reload();
  }, []);

  // Handle rectangle name editing modal
  const handleRectangleNameModalClose = useCallback(() => {
    setShowRectangleNameModal(false);
    setEditingRectangle(null);
  }, []);

  const handleRectangleNameModalSave = useCallback((rectangleId, newName) => {
    console.log('🔄 SAVING RECTANGLE NAME:', { rectangleId, newName });
    
    // Find the shape and update its name
    const shapeToUpdate = shapes.find(shape => shape.id === rectangleId);
    if (shapeToUpdate) {
      console.log('✅ FOUND SHAPE TO UPDATE:', shapeToUpdate);
      
      // Use the updateShape method directly
      if (updateShape) {
        updateShape(rectangleId, { name: newName });
        console.log('✅ SHAPE NAME UPDATED VIA CONTEXT');
      } else {
        console.error('❌ UPDATE SHAPE METHOD NOT AVAILABLE');
      }
    } else {
      console.error('❌ SHAPE NOT FOUND:', rectangleId);
    }
    
    setShowRectangleNameModal(false);
    setEditingRectangle(null);
  }, [shapes, updateShape]);

  // Handle new features
  const handleFlooringPatternSelect = useCallback((pattern) => {
    setSelectedFlooringPattern(pattern);
    // Apply pattern to selected shape if any
    if (selectedShapeId) {
      updateShape(selectedShapeId, { 
        flooringPattern: pattern,
        // Preserve existing properties
        name: shapes.find(s => s.id === selectedShapeId)?.name || 'Room',
        showSquareFootage: shapes.find(s => s.id === selectedShapeId)?.showSquareFootage !== false,
        showInsideDimensions: shapes.find(s => s.id === selectedShapeId)?.showInsideDimensions || false,
        showOutsideDimensions: shapes.find(s => s.id === selectedShapeId)?.showOutsideDimensions || false,
      });
      console.log('✅ Applied flooring pattern:', pattern.name, 'to shape:', selectedShapeId);
    }
  }, [selectedShapeId, updateShape, shapes]);

  const handleHighlightColorSelect = useCallback((color) => {
    setSelectedHighlightColor(color);
    // Apply highlight to selected shape if available
    if (selectedShapeId && updateShape) {
      updateShape(selectedShapeId, { 
        highlightColor: color,
        // Preserve existing properties
        name: shapes.find(s => s.id === selectedShapeId)?.name || 'Room',
        showSquareFootage: shapes.find(s => s.id === selectedShapeId)?.showSquareFootage !== false,
        showInsideDimensions: shapes.find(s => s.id === selectedShapeId)?.showInsideDimensions || false,
        showOutsideDimensions: shapes.find(s => s.id === selectedShapeId)?.showOutsideDimensions || false,
        flooringPattern: shapes.find(s => s.id === selectedShapeId)?.flooringPattern || null,
      });
      console.log('✅ Applied highlight color:', color.name, 'to shape:', selectedShapeId);
    }
  }, [selectedShapeId, updateShape, shapes]);

  const handleHighlightModeToggle = useCallback((enabled) => {
    setIsHighlightMode(enabled);
  }, []);

  const handleDrawingModeToggle = useCallback((enabled) => {
    setIsDrawingMode(enabled);
    console.log('✅ Drawing mode toggled:', enabled);
  }, []);

  const handleAnnotationToolSelect = useCallback((tool) => {
    setSelectedAnnotationTool(tool);
    
    // If arrow tool is selected, set the current tool to arrow
    if (tool.id === 'arrow') {
      // Set the current tool to arrow so canvas knows to create arrows
      window.dispatchEvent(new CustomEvent('setCurrentTool', {
        detail: { tool: 'arrow' }
      }));
    }
  }, []);

  const handleTextAdd = useCallback((text) => {
    // Add text annotation to the canvas at center
    const canvasWidth = window.innerWidth - 400;
    const canvasHeight = window.innerHeight - 100;
    setAnnotations((prev) => [
      ...prev,
      {
        id: `annotation-${Date.now()}`,
        type: 'text',
        text,
        x: canvasWidth / 2,
        y: canvasHeight / 2,
        width: 180,
        height: 40,
        isEditing: false
      }
    ]);
  }, []);

  const handleMeasurementToolSelect = useCallback((tool) => {
    setSelectedMeasurementTool(tool);
  }, []);

  const handleUnitSelect = useCallback((unit) => {
    setSelectedMeasurementUnit(unit);
    console.log('✅ Unit changed to:', unit.name);
  }, []);

  const toggleSquareFootage = useCallback(() => {
    setShowSquareFootage(!showSquareFootage);
    // Update all shapes to show/hide square footage
    shapes.forEach(shape => {
      if (shape.type === 'rectangle') {
        updateShape(shape.id, { showSquareFootage: !showSquareFootage });
      }
    });
  }, [showSquareFootage, shapes, updateShape]);

  const toggleInsideDimensions = useCallback(() => {
    setShowInsideDimensions(!showInsideDimensions);
    // Update all shapes to show/hide inside dimensions
    shapes.forEach(shape => {
      if (shape.type === 'rectangle') {
        updateShape(shape.id, { showInsideDimensions: !showInsideDimensions });
      }
    });
  }, [showInsideDimensions, shapes, updateShape]);

  const toggleOutsideDimensions = useCallback(() => {
    setShowOutsideDimensions(!showOutsideDimensions);
    // Update all shapes to show/hide outside dimensions
    shapes.forEach(shape => {
      if (shape.type === 'rectangle') {
        updateShape(shape.id, { showOutsideDimensions: !showOutsideDimensions });
      }
    });
  }, [showOutsideDimensions, shapes, updateShape]);

  // Grid and blueprint mode toggle functions
  const toggleGrid = useCallback(() => {
    setShowGrid(prev => !prev);
  }, []);

  const toggleBlueprintMode = useCallback(() => {
    setIsBlueprintMode(prev => !prev);
  }, []);

  // Add event listeners for rectangle name editing
  useEffect(() => {
    const handleShowRectangleNameModal = (event) => {
      const { rectangleId, currentName } = event.detail;
      setEditingRectangle({ id: rectangleId, name: currentName });
      setShowRectangleNameModal(true);
    };

    window.addEventListener('showRectangleNameModal', handleShowRectangleNameModal);
    
    return () => {
      window.removeEventListener('showRectangleNameModal', handleShowRectangleNameModal);
    };
  }, []);

  // Handle undo
  const handleUndo = useCallback(() => {
    undo();
  }, [undo]);

  // Handle room deletion
  const handleDeleteRoom = useCallback(() => {
    if (selectedShapeId) {
      const shapeToDelete = shapes.find(shape => shape.id === selectedShapeId);
      if (shapeToDelete && shapeToDelete.type === 'rectangle') {
        deleteShape(selectedShapeId);
        console.log('✅ Deleted room:', selectedShapeId);
      }
    }
  }, [selectedShapeId, shapes, deleteShape]);

  // Handle wall restoration
  const handleRestoreWall = useCallback((lineIndex) => {
    if (selectedShapeId) {
      const shapeToUpdate = shapes.find(shape => shape.id === selectedShapeId);
      if (shapeToUpdate && shapeToUpdate.type === 'rectangle') {
        const currentRemovedLines = shapeToUpdate.removedLines || [];
        const newRemovedLines = currentRemovedLines.filter(index => index !== lineIndex);
        
        updateShape(selectedShapeId, { removedLines: newRemovedLines });
        console.log('✅ Restored wall at index:', lineIndex);
      }
    }
  }, [selectedShapeId, shapes, updateShape]);

  // Handle thickness popup
  const handleThicknessPopupOpen = useCallback(() => {
    setShowThicknessPopup(true);
  }, []);

  const handleThicknessPopupClose = useCallback(() => {
    setShowThicknessPopup(false);
  }, []);

  const handleThicknessSave = useCallback((newThickness) => {
    setCurrentWallThickness(newThickness);
    // Update all walls with new thickness
    shapes.forEach(shape => {
      if (shape.type === 'line' || shape.type === 'rectangle') {
        updateShape(shape.id, { strokeWidth: newThickness });
      }
    });
    console.log('✅ Updated wall thickness to:', newThickness);
  }, [shapes, updateShape]);

  // Handle wall alignment toggle
  const handleWallAlignmentToggle = useCallback(() => {
    setWallAlignmentEnabled(prev => !prev);
    console.log('✅ Wall alignment toggled:', !wallAlignmentEnabled);
  }, [wallAlignmentEnabled]);

  // Handle toolbar toggle
  const handleToolbarToggle = useCallback(() => {
    setIsToolbarVisible(prev => !prev);
    console.log('✅ Toolbar visibility toggled:', !isToolbarVisible);
  }, [isToolbarVisible]);

  const handleToolbarClose = useCallback(() => {
    setIsToolbarVisible(false);
    console.log('✅ Toolbar closed');
  }, []);

  // Handle room name modal
  const handleRoomNameModalOpen = useCallback(() => {
    if (selectedShapeId) {
      const selectedShape = shapes.find(shape => shape.id === selectedShapeId);
      if (selectedShape && selectedShape.type === 'rectangle') {
        setEditingRoomName(selectedShape);
        setShowRoomNameModal(true);
      }
    }
  }, [selectedShapeId, shapes]);

  const handleRoomNameModalClose = useCallback(() => {
    setShowRoomNameModal(false);
    setEditingRoomName(null);
  }, []);

  const handleRoomNameModalSave = useCallback((newName) => {
    if (editingRoomName) {
      updateShape(editingRoomName.id, { name: newName });
      console.log('✅ Updated room name to:', newName);
    }
  }, [editingRoomName, updateShape]);

  // Handle print with trimmed whitespace
  const handlePrint = useCallback(() => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Calculate the bounds of all shapes to trim whitespace
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    shapes.forEach(shape => {
      const left = shape.x;
      const top = shape.y;
      const right = shape.x + (shape.width || 0);
      const bottom = shape.y + (shape.height || 0);
      
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });
    
    // Add padding
    const padding = 50;
    const width = maxX - minX + padding * 2;
    const height = maxY - minY + padding * 2;
    
    // Create print HTML
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Floor Plan</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              background: white;
              font-family: Arial, sans-serif;
            }
            .print-container {
              width: 100vw;
              height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
              overflow: hidden;
            }
            .floor-plan {
              border: 2px solid #333;
              background: white;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }
            @media print {
              body { margin: 0; }
              .print-container { 
                width: 100%; 
                height: 100%; 
                display: block;
              }
              .floor-plan {
                width: 100% !important;
                height: 100% !important;
                border: none;
                box-shadow: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="floor-plan" style="width: ${width}px; height: ${height}px;">
              <canvas id="printCanvas" width="${width}" height="${height}"></canvas>
            </div>
          </div>
          <script>
            // Copy the current canvas content to the print canvas
            const originalCanvas = document.querySelector('canvas');
            const printCanvas = document.getElementById('printCanvas');
            const ctx = printCanvas.getContext('2d');
            
            // Set background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, ${width}, ${height});
            
            // Copy and offset the content
            ctx.save();
            ctx.translate(${padding - minX}, ${padding - minY});
            ctx.drawImage(originalCanvas, 0, 0);
            ctx.restore();
            
            // Auto print
            setTimeout(() => {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
  }, [shapes]);

  // Add arrow creation handler
  const handleArrowAdd = useCallback(() => {
    const canvasWidth = window.innerWidth - 400;
    const canvasHeight = window.innerHeight - 100;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    // Create a default arrow pointing right
    const arrowShape = {
      id: `arrow-${Date.now()}`,
      type: 'arrow',
      x1: centerX - 50,
      y1: centerY,
      x2: centerX + 50,
      y2: centerY,
      color: 0x222222
    };
    
    // Add the arrow shape to the canvas
    window.dispatchEvent(new CustomEvent('addShape', {
      detail: { shape: arrowShape }
    }));
  }, []);

  return (
    <div className={`floor-planner ${screenSize}`}>
      {/* Header - Responsive */}
      <div className={`app-header ${screenSize}`}>
        <div className="header-left">
          <div className="header-logo">
            <img
              src="/floor-planner-logo.jpeg"
              alt="Floor Planner Pro Logo"
              style={{
                height: "80px",
                width: "100%",
                objectFit: "contain",
              }}
              onError={(e) => {
                console.warn("Logo image failed to load:", e.target.src);
                // Fallback to text if image fails to load
                e.target.style.display = "none";
                e.target.parentNode.innerHTML =
                  '<span style="color: #1a1a1a; font-weight: bold; font-size: 24px;">FP</span>';
              }}
            />
          </div>
        </div>

        {/* Navigation - Hidden on mobile, compact on tablet */}
        {screenSize !== "mobile" && (
          <div className="header-nav">
            <button
              className="nav-button"
              onClick={() => navigate("/")}
              title="Home"
            >
              <FaHome />
              {screenSize === "desktop" && <span>Home</span>}
            </button>
            <button
              className="nav-button"
              onClick={() => navigate("/instructions")}
              title="Instructions & Help"
            >
              <FaQuestionCircle />
              {screenSize === "desktop" && <span>Help</span>}
            </button>
          </div>
        )}

        <div className="header-actions">
          {/* Grid and Blueprint Mode Toggles */}
          <button
            className={`nav-button ${showGrid ? 'active' : ''}`}
            onClick={toggleGrid}
            title="Toggle Grid"
          >
            <span className="btn-icon">🟫</span>
            {screenSize === "desktop" && <span>Grid</span>}
          </button>
          <button
            className={`nav-button ${isBlueprintMode ? 'active' : ''}`}
            onClick={toggleBlueprintMode}
            title="Toggle Blueprint Mode"
          >
            <span className="btn-icon">📐</span>
            {screenSize === "desktop" && <span>Blueprint</span>}
          </button>
          
          {screenSize === "desktop" && (
            <Link to="/instructions" className="nav-button">
              <FaBook className="btn-icon" />
              Instructions
            </Link>
          )}
          <button
            className="nav-button"
            onClick={() => {
              // Clear shapes and merged structures from state
              if (typeof removeShapes === 'function' && Array.isArray(shapes)) {
                removeShapes(shapes.map(s => s.id));
              }
              if (typeof setMergedStructures === 'function') {
                setMergedStructures([]);
              }
              // Clear from localStorage
              localStorage.removeItem('react-pixi-floor-planner-state');
              localStorage.removeItem('react-pixi-floor-planner-merged-structures');
              // No alert, no reload
            }}
          >
            <RiDeleteBin2Line className="btn-icon" /> Clear Plan
          </button>
          <button
            className="nav-button"
            onClick={() => window.dispatchEvent(new CustomEvent('showSaveModal'))}
            title="Save Plan"
          >
            <FaSave className="btn-icon" /> Save
          </button>
          <button
            onClick={() => setShowLoadModal(true)}
            className="toolbar-button"
          >
            Load
          </button>
          <button
            onClick={handlePrint}
            className="toolbar-button"
            title="Print floor plan (trimmed to content)"
          >
            🖨️ Print
          </button>
          <Link to="/3d-view" className="action-btn threed-btn">
            <span className="threed-icon">🎯</span>
            <span className="btn-text">
              <span className="btn-main-text">
                {screenSize === "mobile" ? "3D" : "View in 3D"}
              </span>
              {screenSize === "desktop" && (
                <span className="btn-sub-text">Interactive Experience</span>
              )}
            </span>
            <div className="threed-glow"></div>
          </Link>
        </div>
      </div>

      {/* App Content - Responsive Layout */}
      <div className={`app-content ${screenSize}`}>
        <Sidebar />
        <div className={`main-content ${screenSize}`}>
          <Canvas
            ref={canvasRef}
            annotations={annotations}
            setAnnotations={setAnnotations}
            isDrawingMode={isDrawingMode}
            drawingColor={selectedHighlightColor || { hex: 0xFFEB3B }}
            drawingLineWidth={drawingLineWidth}
            selectedMeasurementUnit={selectedMeasurementUnit}
            showGrid={showGrid}
            isBlueprintMode={isBlueprintMode}
          />
          {showRoomManager && <RoomManager />}
        </div>
      </div>

      {/* Floating Analysis Button - Responsive positioning */}
      <button
        className={`floating-analysis-btn ${
          showRoomManager ? "active" : ""
        } ${screenSize}`}
        onClick={toggleRoomManager}
        title={showRoomManager ? "Close Analysis" : "View Analysis"}
      >
        {showRoomManager ? (
          <>
            <FaTimes className="btn-icon" />
            {screenSize !== "mobile" && (
              <span className="btn-text">Close Analysis</span>
            )}
          </>
        ) : (
          <>
            <FaChartBar className="btn-icon" />
            {screenSize !== "mobile" && (
              <span className="btn-text">View Analysis</span>
            )}
          </>
        )}
      </button>

      {/* Popups */}
      <CurvePopup />
      <MergePopup onMergeConfirm={handleMergeConfirm} />
      <UnmergePopup onUnmergeConfirm={handleUnmergeConfirm} />
      <DoorSnapPopup onSnapConfirm={handleDoorSnapConfirm} />
      {isEstimateModalOpen && (
        <EstimateModal
          rooms={generateRoomsData()}
          onClose={() => setIsEstimateModalOpen(false)}
        />
      )}

      {/* Debug Panel - Toggle with Ctrl+Shift+D */}
      {showDebugInfo && (
        <div
          style={{
            position: "fixed",
            top: "80px",
            right: "20px",
            background: "rgba(0, 0, 0, 0.9)",
            color: "white",
            padding: "15px",
            borderRadius: "8px",
            fontSize: "12px",
            zIndex: 10000,
            minWidth: "300px",
            maxWidth: "400px",
            border: "1px solid #333",
          }}
        >
          <div
            style={{
              marginBottom: "10px",
              fontWeight: "bold",
              color: "#4CAF50",
            }}
          >
            🐛 State Persistence Debug Panel
          </div>
          <div style={{ marginBottom: "8px" }}>
            <strong>📊 Current State:</strong>
          </div>
          <div style={{ marginLeft: "10px", marginBottom: "8px" }}>
            • Shapes: {shapes.length}
            <br />• Merged Structures:{" "}
            {mergedStructures ? mergedStructures.length : 0}
            <br />
            {mergedStructures && mergedStructures.length > 0 && (
              <>
                • Complex MergedStructures:{" "}
                {
                  mergedStructures.filter(
                    (ms) =>
                      ms.snappedSideLines?.length > 0 ||
                      ms.gapFillerLines?.length > 0 ||
                      Object.keys(ms.deletedSnappedLines || {}).length > 0
                  ).length
                }
                <br />• Total Snapped Lines:{" "}
                {mergedStructures.reduce(
                  (sum, ms) => sum + (ms.snappedSideLines?.length || 0),
                  0
                )}
                <br />• Total Gap Fillers:{" "}
                {mergedStructures.reduce(
                  (sum, ms) => sum + (ms.gapFillerLines?.length || 0),
                  0
                )}
                <br />• Deletion Tracking:{" "}
                {mergedStructures.reduce(
                  (sum, ms) =>
                    sum +
                    Object.keys(ms.deletedSnappedLines || {}).length +
                    Object.keys(ms.deletedGapFillers || {}).length,
                  0
                )}{" "}
                items
                <br />
              </>
            )}
            • Selected: {selectedShapeId || "None"}
          </div>
          <div style={{ marginBottom: "8px" }}>
            <strong>💾 Storage Status:</strong>
          </div>
          <div style={{ marginLeft: "10px", marginBottom: "10px" }}>
            • Main State:{" "}
            {localStorage.getItem("react-pixi-floor-planner-state")
              ? "✅ Saved"
              : "❌ Empty"}
            <br />• Merged Structures:{" "}
            {localStorage.getItem("react-pixi-floor-planner-merged-structures")
              ? "✅ Saved"
              : "❌ Empty"}
          </div>
          <div style={{ display: "flex", gap: "8px", fontSize: "11px" }}>
            <button
              onClick={() => setShowDebugInfo(false)}
              style={{
                background: "#666",
                color: "white",
                border: "none",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Close
            </button>
            <button
              onClick={() => {
                clearStoredState();
                alert("🗑️ Storage cleared! Refresh page to test persistence.");
              }}
              style={{
                background: "#f44336",
                color: "white",
                border: "none",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Clear Storage
            </button>
            <button
              onClick={() => {
                console.log("🔍 CURRENT STORAGE CONTENTS:");
                console.log(
                  "Main State:",
                  localStorage.getItem("react-pixi-floor-planner-state")
                );
                console.log(
                  "Merged Structures:",
                  localStorage.getItem(
                    "react-pixi-floor-planner-merged-structures"
                  )
                );
              }}
              style={{
                background: "#2196F3",
                color: "white",
                border: "none",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Log Storage
            </button>
            <button
              onClick={() => {
                // Test persistence by triggering a manual save
                console.log("🧪 TESTING PERSISTENCE - Current State:");
                console.log("Shapes:", shapes.length);
                console.log("MergedStructures:", mergedStructures);
                console.log(
                  "Complex data details:",
                  mergedStructures?.map((ms) => ({
                    id: ms.id,
                    snappedLines: ms.snappedSideLines?.length || 0,
                    gapFillers: ms.gapFillerLines?.length || 0,
                    deletedTracking: Object.keys(ms.deletedSnappedLines || {})
                      .length,
                  }))
                );

                // Simulate page refresh warning
                alert(
                  "🧪 Persistence Test:\n\nRefresh the page to test if all data (including MergedStructures with lines/curves) is preserved.\n\nCheck console for detailed restoration logs."
                );
              }}
              style={{
                background: "#9C27B0",
                color: "white",
                border: "none",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Test Persistence
            </button>
          </div>
          <div style={{ marginTop: "10px", fontSize: "10px", color: "#999" }}>
            Press Ctrl+Shift+D to toggle this panel
          </div>
        </div>
      )}

      {/* Save and Load Modals */}
      {showSaveModal && (
        <SaveModal
          onClose={handleSaveModalClose}
          onSave={handleSaveModalSave}
        />
      )}
      
      {showLoadModal && (
        <LoadModal
          onClose={handleLoadModalClose}
          onLoad={handleLoadModalLoad}
        />
      )}

      {/* Rectangle Name Editing Modal */}
      {showRectangleNameModal && editingRectangle && (
        <RectangleNameModal
          isOpen={showRectangleNameModal}
          onClose={handleRectangleNameModalClose}
          onSave={handleRectangleNameModalSave}
          currentName={editingRectangle.name}
          rectangleId={editingRectangle.id}
        />
      )}

      {/* Thickness Popup */}
      <ThicknessPopup
        isOpen={showThicknessPopup}
        onClose={handleThicknessPopupClose}
        onSave={handleThicknessSave}
        currentThickness={currentWallThickness}
      />

      {/* Room Name Modal */}
      <RoomNameModal
        isOpen={showRoomNameModal}
        onClose={handleRoomNameModalClose}
        onSave={handleRoomNameModalSave}
        currentName={editingRoomName?.name || ''}
        roomId={editingRoomName?.id}
      />

      {/* Total Square Footage Display */}
      <TotalSquareFootage
        shapes={shapes}
        mergedStructures={mergedStructures}
      />

      {/* Toolbar Toggle Button */}
      <button
        className="toolbar-toggle-button"
        onClick={handleToolbarToggle}
        title="Open Tools"
      >
        <FaBars />
      </button>

      {/* Conditional Toolbar */}
      {isToolbarVisible && (
        <div className="toolbar-overlay">
          <div className="toolbar">
            <div className="toolbar-header">
              <h3>Tools</h3>
              <button
                className="toolbar-close-button"
                onClick={handleToolbarClose}
                title="Close Tools"
              >
                <FaTimes />
              </button>
            </div>

            <div className="toolbar-content">
              <div className="toolbar-section">
                <button
                  onClick={() => setIsEstimateModalOpen(true)}
                  className="toolbar-button"
                >
                  <FaQuestionCircle />
                  Estimate
                </button>
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="toolbar-button"
                >
                  Save
                </button>
                <button
                  onClick={() => setShowLoadModal(true)}
                  className="toolbar-button"
                >
                  Load
                </button>
                <button
                  onClick={handlePrint}
                  className="toolbar-button"
                  title="Print floor plan (trimmed to content)"
                >
                  🖨️ Print
                </button>
              </div>

              <div className="toolbar-section">
                <FlooringPatterns
                  onPatternSelect={handleFlooringPatternSelect}
                  selectedPattern={selectedFlooringPattern}
                  selectedShapeId={selectedShapeId}
                  updateShape={updateShape}
                  shapes={shapes}
                  mergedStructures={mergedStructures}
                />
                <Highlighter
                  onColorSelect={handleHighlightColorSelect}
                  selectedColor={selectedHighlightColor}
                  onHighlightModeToggle={handleHighlightModeToggle}
                  isHighlightMode={isHighlightMode}
                  onDrawingModeToggle={(enabled) => setIsDrawingMode(enabled)}
                  isDrawingMode={isDrawingMode}
                  selectedShapeId={selectedShapeId}
                  updateShape={updateShape}
                  shapes={shapes}
                />
                <AnnotationTools
                  onToolSelect={handleAnnotationToolSelect}
                  selectedTool={selectedAnnotationTool}
                  onTextAdd={handleTextAdd}
                />
                <MeasurementTools
                  onUnitSelect={(unit) => {
                    setSelectedMeasurementUnit(unit);
                    console.log('✅ Unit changed to:', unit.name);
                  }}
                  selectedUnit={selectedMeasurementUnit}
                />
              </div>

              <div className="toolbar-section">
                <button
                  onClick={toggleSquareFootage}
                  className={`toolbar-button ${showSquareFootage ? 'active' : ''}`}
                >
                  Square Footage
                </button>
                <button
                  onClick={toggleInsideDimensions}
                  className={`toolbar-button ${showInsideDimensions ? 'active' : ''}`}
                >
                  Inside Dims
                </button>
                <button
                  onClick={toggleOutsideDimensions}
                  className={`toolbar-button ${showOutsideDimensions ? 'active' : ''}`}
                >
                  Outside Dims
                </button>
              </div>

              <div className="toolbar-section">
                <h3>Room Tools</h3>
                <button 
                  className="toolbar-button"
                  onClick={handleDeleteRoom}
                  disabled={!selectedShapeId}
                  title="Delete selected room"
                >
                  🗑️ Delete Room
                </button>
                <button 
                  className="toolbar-button"
                  onClick={handleRestoreWall}
                  disabled={!selectedShapeId}
                  title="Restore selected wall"
                >
                  🔄 Restore Wall
                </button>
                <button 
                  className="toolbar-button"
                  onClick={handleUndo}
                  title="Undo last action"
                >
                  ↩️ Undo
                </button>
                <button 
                  className="toolbar-button"
                  onClick={handleThicknessPopupOpen}
                  title="Adjust wall thickness"
                >
                  📏 Wall Thickness
                </button>
                <button 
                  className={`toolbar-button ${wallAlignmentEnabled ? 'active' : ''}`}
                  onClick={handleWallAlignmentToggle}
                  title="Toggle wall alignment"
                >
                  {wallAlignmentEnabled ? '🔒' : '🔓'} Wall Alignment
                </button>
                <button 
                  className="toolbar-button"
                  onClick={handleRoomNameModalOpen}
                  disabled={!selectedShapeId}
                  title="Name selected room"
                >
                  🏷️ Name Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlanner;
